/**
 * @fileoverview Cold Room Ventilation Heat Loss Module
 * 
 * Interactive educational component teaching ventilation heat loss concepts
 * in cold (unheated) rooms and their impact on heat pump system sizing.
 * 
 * @module CRVentilation
 * @requires react
 * @requires antd
 * @requires react-router-dom
 * 
 * @description
 * The simulation progresses through five educational steps:
 * 1. Introduction to Ventilation - Air exchange concepts and importance
 * 2. Ventilation Heat Loss Formula - Mathematical foundations
 * 3. Air Change Rates - Understanding n-values for different scenarios
 * 4. Interactive Calculator - Hands-on ventilation loss calculations
 * 5. Heat Pump Impact - Effect on COP and system sizing
 * 
 * @author HeatCraftHP Team
 * @version 1.0.0
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Space, Slider, InputNumber, Card, Row, Col, Select, Table, Tooltip, Progress, Divider, Alert } from 'antd';
import { FilePdfOutlined, ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

// Shared components
import {
  HPTitle,
  NamedItemComponent,
  MrHP,
  QoutationElement,
  CourseNavigationModal,
} from '../../../Shared';

// Other shared components
import { LatexRenderer } from '../../../Shared/LatexRenderer';

// Constants
import {
  FIRST_STEP,
  SECOND_STEP,
  THIRD_STEP,
  FOURTH_STEP,
  FIFTH_STEP,
  AIR_HEAT_CAPACITY,
  DEFAULT_ROOM_LENGTH,
  DEFAULT_ROOM_WIDTH,
  DEFAULT_ROOM_HEIGHT,
  DEFAULT_INDOOR_TEMP,
  DEFAULT_OUTDOOR_TEMP,
  DEFAULT_ACH,
  HEAT_RECOVERY,
  FORMULAS,
  COLORS,
  ANIMATION_INTERVAL_MS,
} from './constants';

// PDF Documents
import VentilationBasicsDocument from './HP_Ventilation_Basics.pdf';
import VentilationCalculationsDocument from './HP_Ventilation_Calculations.pdf';
import HeatRecoveryDocument from './HP_Heat_Recovery_Systems.pdf';
import PagesWrapper from '../../../../PagesWrapper';
import { courseStructure } from '../../Intro/courseStructure';

// Styles
import styles from './ColdRoomVentilation.module.css';

/**
 * Educational resource documents configuration
 * @constant {Array<{name: string, document: string}>}
 */
const RESOURCES = [
  {
    name: 'Ventilation Fundamentals',
    document: VentilationBasicsDocument,
  },
  {
    name: 'Ventilation Heat Loss Calculations',
    document: VentilationCalculationsDocument,
  },
  {
    name: 'Heat Recovery Systems',
    document: HeatRecoveryDocument,
  },
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate ventilation heat loss
 * Q = 0.34 × V × n × ΔT (simplified formula)
 * 
 * @param {number} volume - Room volume in m³
 * @param {number} ach - Air changes per hour (1/h)
 * @param {number} deltaT - Temperature difference in K
 * @param {number} heatRecoveryEfficiency - Heat recovery efficiency (0-1)
 * @returns {number} Heat loss in Watts
 */
const calculateVentilationLoss = (volume, ach, deltaT, heatRecoveryEfficiency = 0) => {
  const baseLoss = AIR_HEAT_CAPACITY * volume * ach * deltaT;
  return baseLoss * (1 - heatRecoveryEfficiency);
};

/**
 * Calculate volume flow rate
 * @param {number} volume - Room volume in m³
 * @param {number} ach - Air changes per hour
 * @returns {number} Volume flow rate in m³/h
 */
const calculateVolumeFlow = (volume, ach) => volume * ach;

/**
 * Format number with appropriate precision
 * @param {number} value - Number to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted number
 */
const formatNumber = (value, decimals = 1) => {
  if (value >= 1000) return `${(value / 1000).toFixed(decimals)} k`;
  return value.toFixed(decimals);
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Main ventilation heat loss simulation component
 * 
 * @component
 * @returns {JSX.Element} Complete ventilation simulation page
 */
export function CRVentilation() {
  const location = useLocation();
  const [simulationStep, setSimulationStep] = useState(FIRST_STEP);
  const [showCourseNav, setShowCourseNav] = useState(false);

  // Room parameters
  const [roomLength, setRoomLength] = useState(DEFAULT_ROOM_LENGTH);
  const [roomWidth, setRoomWidth] = useState(DEFAULT_ROOM_WIDTH);
  const [roomHeight, setRoomHeight] = useState(DEFAULT_ROOM_HEIGHT);

  // Ventilation parameters
  const [ach, setAch] = useState(DEFAULT_ACH);
  const [indoorTemp, setIndoorTemp] = useState(DEFAULT_INDOOR_TEMP);
  const [outdoorTemp, setOutdoorTemp] = useState(DEFAULT_OUTDOOR_TEMP);
  const [heatRecovery, setHeatRecovery] = useState('none');

  // Animation state
  const [animationProgress, setAnimationProgress] = useState(0);

  // Computed values
  const roomVolume = useMemo(() => 
    roomLength * roomWidth * roomHeight, 
    [roomLength, roomWidth, roomHeight]
  );

  const deltaT = useMemo(() => 
    indoorTemp - outdoorTemp, 
    [indoorTemp, outdoorTemp]
  );

  const heatRecoveryEfficiency = HEAT_RECOVERY[heatRecovery]?.efficiency || 0;

  const ventilationLoss = useMemo(() => 
    calculateVentilationLoss(roomVolume, ach, deltaT, heatRecoveryEfficiency),
    [roomVolume, ach, deltaT, heatRecoveryEfficiency]
  );

  const volumeFlowRate = useMemo(() => 
    calculateVolumeFlow(roomVolume, ach),
    [roomVolume, ach]
  );

  // Animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationProgress((prev) => (prev + 1) % 100);
    }, ANIMATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Navigation handlers
  const goToStep = useCallback((step) => {
    setSimulationStep(step);
  }, []);

  // Render step content
  const renderCurrentStep = () => {
    switch (simulationStep) {
      case FIRST_STEP:
        return (
          <FirstStep
            animationProgress={animationProgress}
            onNext={() => goToStep(SECOND_STEP)}
          />
        );
      case SECOND_STEP:
        return (
          <SecondStep
            onPrevious={() => goToStep(FIRST_STEP)}
            onNext={() => goToStep(THIRD_STEP)}
          />
        );
      case THIRD_STEP:
        return (
          <ThirdStep
            onPrevious={() => goToStep(SECOND_STEP)}
            onNext={() => goToStep(FOURTH_STEP)}
          />
        );
      case FOURTH_STEP:
        return (
          <FourthStep
            roomLength={roomLength}
            roomWidth={roomWidth}
            roomHeight={roomHeight}
            roomVolume={roomVolume}
            ach={ach}
            indoorTemp={indoorTemp}
            outdoorTemp={outdoorTemp}
            deltaT={deltaT}
            heatRecovery={heatRecovery}
            heatRecoveryEfficiency={heatRecoveryEfficiency}
            ventilationLoss={ventilationLoss}
            volumeFlowRate={volumeFlowRate}
            setRoomLength={setRoomLength}
            setRoomWidth={setRoomWidth}
            setRoomHeight={setRoomHeight}
            setAch={setAch}
            setIndoorTemp={setIndoorTemp}
            setOutdoorTemp={setOutdoorTemp}
            setHeatRecovery={setHeatRecovery}
            onPrevious={() => goToStep(THIRD_STEP)}
            onNext={() => goToStep(FIFTH_STEP)}
          />
        );
      case FIFTH_STEP:
        return (
          <FifthStep
            ventilationLoss={ventilationLoss}
            heatRecoveryEfficiency={heatRecoveryEfficiency}
            onPrevious={() => goToStep(FOURTH_STEP)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PagesWrapper>
      <HPTitle 
        name="1. Cold room" 
        subname="1.3 Ventilation"
        onTitleClick={() => setShowCourseNav(true)}
      >
        <Space direction="vertical" size="small" align="start">
          <small className="default-gray">Resources</small>
          <Space>
            {RESOURCES.map((resource) => (
              <NamedItemComponent
                key={resource.name}
                name={resource.name}
                onClick={() => window.open(resource.document)}
              >
                <FilePdfOutlined className="default-red default-x-larger hq-clickable" />
              </NamedItemComponent>
            ))}
          </Space>
        </Space>
      </HPTitle>

      <CourseNavigationModal
        open={showCourseNav}
        onClose={() => setShowCourseNav(false)}
        courseStructure={courseStructure}
        currentPath={location.pathname}
      />

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
        <Space align="start">{renderCurrentStep()}</Space>
      </div>
    </PagesWrapper>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

/**
 * MrHP mascot section
 */
function MrHPSection() {
  return (
    <div>
      <svg
        width={150}
        height={410}
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: 50 }}
      >
        <MrHP y={0} x={0} width={135} />
      </svg>
    </div>
  );
}

/**
 * Navigation button group
 */
function NavigationButtons({ onPrevious, onNext }) {
  return (
    <Space>
      {onPrevious && (
        <Button size="small" type="primary" onClick={onPrevious}>
          Previous
        </Button>
      )}
      {onNext && (
        <Button size="small" type="primary" onClick={onNext}>
          Next
        </Button>
      )}
    </Space>
  );
}

/**
 * Air particle animation component
 */
function AirParticles({ count = 20, direction = 'out', color = COLORS.warmAir, progress = 0 }) {
  const particles = useMemo(() => 
    Array(count).fill(0).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 60 + 20,
      speed: 0.5 + Math.random() * 0.5,
      size: 3 + Math.random() * 4,
    })),
    [count]
  );

  return (
    <g className={styles.particleGroup}>
      {particles.map((p) => {
        const offset = (progress * p.speed) % 100;
        const xPos = direction === 'out' 
          ? (p.x + offset) % 100 
          : 100 - ((p.x + offset) % 100);
        return (
          <circle
            key={p.id}
            cx={xPos}
            cy={p.y}
            r={p.size}
            fill={color}
            opacity={0.6 + Math.sin(progress * 0.1 + p.id) * 0.3}
          />
        );
      })}
    </g>
  );
}

// ============================================================================
// Step 1: Introduction to Ventilation
// ============================================================================

function FirstStep({ animationProgress, onNext }) {
  return (
    <div className={styles.stepContainer}>
      <Space align="start" size="large">
        {/* Room visualization with air flow */}
        <div className={styles.visualizationContainer}>
          <svg width={400} height={300} viewBox="0 0 400 300" className={styles.roomSvg}>
            {/* Room outline */}
            <rect x={50} y={50} width={300} height={200} fill="#f8f9fa" stroke={COLORS.wall} strokeWidth={3} />
            
            {/* Window opening */}
            <rect x={280} y={80} width={70} height={80} fill={COLORS.window} stroke={COLORS.wall} strokeWidth={2} />
            <line x1={315} y1={80} x2={315} y2={160} stroke={COLORS.wall} strokeWidth={1} />
            <line x1={280} y1={120} x2={350} y2={120} stroke={COLORS.wall} strokeWidth={1} />
            
            {/* Warm air escaping (red arrows going out) */}
            <g transform="translate(280, 90)">
              <AirParticles count={15} direction="out" color={COLORS.warmAir} progress={animationProgress} />
            </g>
            
            {/* Cold air entering (blue arrows coming in) */}
            <g transform="translate(280, 130)">
              <AirParticles count={15} direction="in" color={COLORS.coldAir} progress={animationProgress} />
            </g>
            
            {/* Labels */}
            <text x={370} y={95} fill={COLORS.warmAir} fontSize={12} fontWeight="bold">Warm air out</text>
            <text x={370} y={155} fill={COLORS.coldAir} fontSize={12} fontWeight="bold">Cold air in</text>
            
            {/* Indoor temperature label */}
            <text x={100} y={150} fill={COLORS.text.primary} fontSize={14}>T_inside = 20°C</text>
            
            {/* Outdoor temperature label */}
            <text x={320} y={270} fill={COLORS.coldAir} fontSize={12}>T_outside = -10°C</text>
            
            {/* Title */}
            <text x={200} y={30} textAnchor="middle" fill={COLORS.text.primary} fontSize={16} fontWeight="bold">
              Air Exchange Through Window
            </text>
          </svg>
        </div>

        <MrHPSection />

        <Space direction="vertical" align='start' size="middle">
          <Space size="middle" align="start">
            <Card className={styles.conceptCard}>
              <h3>What is Ventilation Heat Loss?</h3>
              <p>
                <strong>Ventilation heat loss</strong> occurs when warm indoor air is replaced by 
                cold outdoor air. This happens through:
              </p>
              <ul>
                <li><strong>Natural ventilation:</strong> Opening windows and doors</li>
                <li><strong>Infiltration:</strong> Uncontrolled air leakage through gaps and cracks</li>
                <li><strong>Mechanical ventilation:</strong> Fans and HVAC systems</li>
              </ul>
            </Card>

            <Card className={styles.conceptCard}>
              <h3>Why Does It Matter for Heat Pumps?</h3>
              <p>
                Ventilation can account for <strong>30-50%</strong> of total heat loss in modern, 
                well-insulated buildings. For heat pump systems, this means:
              </p>
              <ul>
                <li>Increased heating demand</li>
                <li>Higher electricity consumption</li>
                <li>Larger system sizing requirements</li>
                <li>Lower effective COP if not managed properly</li>
              </ul>
            </Card>
          </Space>
          <QoutationElement texts={["In a passive house, ventilation losses can exceed transmission losses through the building envelope."]} >
            <NavigationButtons onNext={onNext} />
          </QoutationElement>
        </Space>
      </Space>
    </div>
  );
}

// ============================================================================
// Step 2: Ventilation Heat Loss Formula
// ============================================================================

function SecondStep({ onPrevious, onNext }) {
  return (
    <div className={styles.stepContainer}>
      <Space align="start" size="large">
        <Space direction="vertical" size="middle" style={{ maxWidth: 500 }}>
          <Card className={styles.formulaCard}>
            <h3>The Ventilation Heat Loss Formula</h3>
            <div className={styles.mainFormula}>
              <LatexRenderer latex={FORMULAS.ventilation_simplified} displayMode />
            </div>
            
            <Divider />
            
            <h4>Where:</h4>
            <table className={styles.variableTable}>
              <tbody>
                <tr>
                  <td><LatexRenderer latex="Q_{vent}" /></td>
                  <td>=</td>
                  <td>Ventilation heat loss [W]</td>
                </tr>
                <tr>
                  <td><LatexRenderer latex="0.34" /></td>
                  <td>=</td>
                  <td>Volumetric heat capacity of air [Wh/(m³·K)]</td>
                </tr>
                <tr>
                  <td><LatexRenderer latex="V" /></td>
                  <td>=</td>
                  <td>Room volume [m³]</td>
                </tr>
                <tr>
                  <td><LatexRenderer latex="n" /></td>
                  <td>=</td>
                  <td>Air change rate [1/h]</td>
                </tr>
                <tr>
                  <td><LatexRenderer latex="\Delta T" /></td>
                  <td>=</td>
                  <td>Temperature difference [K or °C]</td>
                </tr>
              </tbody>
            </table>
          </Card>

          <Card className={styles.derivationCard}>
            <h4>Derivation from First Principles</h4>
            <p>Starting with the general heat transfer equation:</p>
            <div className={styles.formula}>
              <LatexRenderer latex={FORMULAS.ventilation_heat_loss} displayMode />
            </div>
            <p>Where:</p>
            <ul>
              <li><LatexRenderer latex="\dot{V}" /> = Volume flow rate [m³/h]</li>
              <li><LatexRenderer latex="\rho" /> = Air density ≈ 1.2 kg/m³</li>
              <li><LatexRenderer latex="c_p" /> = Specific heat ≈ 1005 J/(kg·K)</li>
            </ul>
            <p>
              Since <LatexRenderer latex="\rho \cdot c_p / 3600 \approx 0.34" /> Wh/(m³·K), 
              we get the simplified formula.
            </p>
          </Card>

          <Alert
            message="Key Insight"
            description="The ventilation heat loss is proportional to both room volume and air change rate. 
            Reducing either one will decrease the heat loss proportionally."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />

          <NavigationButtons onPrevious={onPrevious} onNext={onNext} />
        </Space>

        <MrHPSection />
      </Space>
    </div>
  );
}

// ============================================================================
// Step 3: Air Change Rates
// ============================================================================

function ThirdStep({ onPrevious, onNext }) {
  const achData = [
    { key: '1', scenario: 'Passive House', ach: '0.1 - 0.3', typical: 0.2, description: 'Minimal infiltration with HRV' },
    { key: '2', scenario: 'Modern Sealed Building', ach: '0.3 - 0.5', typical: 0.4, description: 'Good airtightness' },
    { key: '3', scenario: 'Standard New Building', ach: '0.4 - 0.7', typical: 0.5, description: 'Code-compliant construction' },
    { key: '4', scenario: 'Older Building (pre-1990)', ach: '0.6 - 1.2', typical: 0.8, description: 'Average airtightness' },
    { key: '5', scenario: 'Leaky Old Building', ach: '1.0 - 2.0', typical: 1.5, description: 'Poor airtightness' },
    { key: '6', scenario: 'Tilted Window', ach: '0.5 - 1.5', typical: 1.0, description: 'Controlled natural ventilation' },
    { key: '7', scenario: 'Fully Open Window', ach: '10 - 20', typical: 15, description: 'Burst ventilation' },
    { key: '8', scenario: 'Cross Ventilation', ach: '20 - 40', typical: 30, description: 'Maximum air exchange' },
  ];

  const columns = [
    { title: 'Scenario', dataIndex: 'scenario', key: 'scenario' },
    { title: 'ACH Range (1/h)', dataIndex: 'ach', key: 'ach' },
    { title: 'Typical Value', dataIndex: 'typical', key: 'typical' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
  ];

  return (
    <div className={styles.stepContainer}>
      <Space direction="vertical" size="middle" style={{ maxWidth: 900 }}>
        <Card className={styles.conceptCard}>
          <h3>Understanding Air Change Rate (n)</h3>
          <p>
            The <strong>air change rate (n)</strong> indicates how many times the complete volume 
            of air in a room is replaced with fresh outdoor air per hour. It is measured in 
            <strong> 1/h</strong> or <strong>ACH</strong> (Air Changes per Hour).
          </p>
          <div className={styles.formula}>
            <LatexRenderer latex={FORMULAS.air_change_rate} displayMode />
          </div>
        </Card>

        <Card className={styles.tableCard}>
          <h4>Typical Air Change Rates</h4>
          <Table 
            dataSource={achData} 
            columns={columns} 
            pagination={false}
            size="small"
            rowClassName={(record) => 
              record.typical > 1 ? styles.highAch : record.typical < 0.5 ? styles.lowAch : ''
            }
          />
        </Card>

        <Row gutter={16}>
          <Col span={12}>
            <Card className={styles.comparisonCard}>
              <h4 style={{ color: COLORS.coldAir }}>Low ACH (0.2-0.5)</h4>
              <ul>
                <li>✅ Lower ventilation heat loss</li>
                <li>✅ Better energy efficiency</li>
                <li>⚠️ May require mechanical ventilation</li>
                <li>⚠️ Indoor air quality concerns</li>
              </ul>
            </Card>
          </Col>
          <Col span={12}>
            <Card className={styles.comparisonCard}>
              <h4 style={{ color: COLORS.warmAir }}>High ACH (1.0+)</h4>
              <ul>
                <li>✅ Good indoor air quality</li>
                <li>✅ Moisture control</li>
                <li>❌ High ventilation heat loss</li>
                <li>❌ Increased heating demand</li>
              </ul>
            </Card>
          </Col>
        </Row>

        <Alert
          message="Building Code Requirements"
          description="Most building codes require a minimum ventilation rate of 0.3-0.5 ACH or 
          25-30 m³/h per person for indoor air quality. This creates a baseline ventilation heat loss 
          that cannot be eliminated but can be reduced using heat recovery ventilation (HRV)."
          type="warning"
          showIcon
        />

        <NavigationButtons onPrevious={onPrevious} onNext={onNext} />
      </Space>
    </div>
  );
}

// ============================================================================
// Step 4: Interactive Calculator
// ============================================================================

function FourthStep({
  roomLength, roomWidth, roomHeight, roomVolume,
  ach, indoorTemp, outdoorTemp, deltaT,
  heatRecovery, heatRecoveryEfficiency, ventilationLoss, volumeFlowRate,
  setRoomLength, setRoomWidth, setRoomHeight,
  setAch, setIndoorTemp, setOutdoorTemp, setHeatRecovery,
  onPrevious, onNext,
}) {
  const ventilationLossWithoutHR = AIR_HEAT_CAPACITY * roomVolume * ach * deltaT;
  const heatRecovered = ventilationLossWithoutHR - ventilationLoss;

  return (
    <div className={styles.stepContainer}>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 1000 }}>
        <h3>Interactive Ventilation Calculator</h3>

        <Row gutter={24}>
          {/* Input Panel */}
          <Col xs={24} md={12}>
            <Card title="Room Parameters" className={styles.inputCard}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div className={styles.inputGroup}>
                  <label>Room Length (m)</label>
                  <Slider
                    min={2}
                    max={15}
                    step={0.5}
                    value={roomLength}
                    onChange={setRoomLength}
                    marks={{ 2: '2', 5: '5', 10: '10', 15: '15' }}
                  />
                  <InputNumber
                    min={2}
                    max={15}
                    step={0.5}
                    value={roomLength}
                    onChange={setRoomLength}
                    addonAfter="m"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>Room Width (m)</label>
                  <Slider
                    min={2}
                    max={10}
                    step={0.5}
                    value={roomWidth}
                    onChange={setRoomWidth}
                    marks={{ 2: '2', 4: '4', 6: '6', 8: '8', 10: '10' }}
                  />
                  <InputNumber
                    min={2}
                    max={10}
                    step={0.5}
                    value={roomWidth}
                    onChange={setRoomWidth}
                    addonAfter="m"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>Room Height (m)</label>
                  <Slider
                    min={2}
                    max={4}
                    step={0.1}
                    value={roomHeight}
                    onChange={setRoomHeight}
                    marks={{ 2: '2', 2.5: '2.5', 3: '3', 3.5: '3.5', 4: '4' }}
                  />
                  <InputNumber
                    min={2}
                    max={4}
                    step={0.1}
                    value={roomHeight}
                    onChange={setRoomHeight}
                    addonAfter="m"
                  />
                </div>

                <Divider />

                <div className={styles.inputGroup}>
                  <label>Air Change Rate (1/h)</label>
                  <Slider
                    min={0.1}
                    max={3}
                    step={0.1}
                    value={ach}
                    onChange={setAch}
                    marks={{ 0.1: '0.1', 0.5: '0.5', 1: '1', 2: '2', 3: '3' }}
                  />
                  <InputNumber
                    min={0.1}
                    max={3}
                    step={0.1}
                    value={ach}
                    onChange={setAch}
                    addonAfter="1/h"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>Indoor Temperature (°C)</label>
                  <Slider
                    min={15}
                    max={25}
                    value={indoorTemp}
                    onChange={setIndoorTemp}
                    marks={{ 15: '15', 20: '20', 25: '25' }}
                  />
                  <InputNumber
                    min={15}
                    max={25}
                    value={indoorTemp}
                    onChange={setIndoorTemp}
                    addonAfter="°C"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>Outdoor Temperature (°C)</label>
                  <Slider
                    min={-20}
                    max={10}
                    value={outdoorTemp}
                    onChange={setOutdoorTemp}
                    marks={{ '-20': '-20', '-10': '-10', 0: '0', 10: '10' }}
                  />
                  <InputNumber
                    min={-20}
                    max={10}
                    value={outdoorTemp}
                    onChange={setOutdoorTemp}
                    addonAfter="°C"
                  />
                </div>

                <Divider />

                <div className={styles.inputGroup}>
                  <label>Heat Recovery System</label>
                  <Select
                    value={heatRecovery}
                    onChange={setHeatRecovery}
                    style={{ width: '100%' }}
                    options={Object.entries(HEAT_RECOVERY).map(([key, val]) => ({
                      value: key,
                      label: val.label,
                    }))}
                  />
                </div>
              </Space>
            </Card>
          </Col>

          {/* Results Panel */}
          <Col xs={24} md={12}>
            <Card title="Calculation Results" className={styles.resultsCard}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Room Volume:</span>
                  <span className={styles.resultValue}>{roomVolume.toFixed(1)} m³</span>
                </div>

                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Temperature Difference:</span>
                  <span className={styles.resultValue}>{deltaT} K</span>
                </div>

                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Volume Flow Rate:</span>
                  <span className={styles.resultValue}>{volumeFlowRate.toFixed(1)} m³/h</span>
                </div>

                <Divider />

                <div className={styles.resultItemLarge}>
                  <span className={styles.resultLabel}>Ventilation Heat Loss (no HR):</span>
                  <span className={styles.resultValueLarge} style={{ color: COLORS.warmAir }}>
                    {formatNumber(ventilationLossWithoutHR)} W
                  </span>
                </div>

                {heatRecoveryEfficiency > 0 && (
                  <>
                    <div className={styles.resultItem}>
                      <span className={styles.resultLabel}>Heat Recovered:</span>
                      <span className={styles.resultValue} style={{ color: COLORS.outdoor }}>
                        -{formatNumber(heatRecovered)} W
                      </span>
                    </div>

                    <div className={styles.resultItemLarge}>
                      <span className={styles.resultLabel}>Net Ventilation Loss:</span>
                      <span className={styles.resultValueLarge} style={{ color: '#52c41a' }}>
                        {formatNumber(ventilationLoss)} W
                      </span>
                    </div>

                    <Progress 
                      percent={Math.round(heatRecoveryEfficiency * 100)} 
                      status="active"
                      strokeColor="#52c41a"
                      format={(percent) => `${percent}% recovered`}
                    />
                  </>
                )}

                <Divider />

                <div className={styles.formulaDisplay}>
                  <small>Calculation:</small>
                  <LatexRenderer 
                    latex={`Q_{vent} = 0.34 \\times ${roomVolume.toFixed(1)} \\times ${ach} \\times ${deltaT} = ${formatNumber(ventilationLossWithoutHR)} \\text{ W}`} 
                  />
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        <NavigationButtons onPrevious={onPrevious} onNext={onNext} />
      </Space>
    </div>
  );
}

// ============================================================================
// Step 5: Heat Pump Impact
// ============================================================================

function FifthStep({ ventilationLoss, heatRecoveryEfficiency, onPrevious }) {
  const [cop, setCop] = useState(3.5);
  
  const electricalPower = ventilationLoss / cop;
  const dailyEnergy = (electricalPower * 24) / 1000; // kWh
  const monthlyEnergy = dailyEnergy * 30;
  const seasonalEnergy = dailyEnergy * 180; // 6 month heating season

  const copValues = [
    { cop: 2.5, label: 'Low (air-source, cold climate)', color: '#ff4d4f' },
    { cop: 3.5, label: 'Average (air-source, mild climate)', color: '#faad14' },
    { cop: 4.5, label: 'Good (ground-source)', color: '#52c41a' },
    { cop: 5.5, label: 'Excellent (ground-source, optimized)', color: '#1890ff' },
  ];

  return (
    <div className={styles.stepContainer}>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 1000 }}>
        <h3>Impact on Heat Pump System</h3>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card title="Ventilation Load on Heat Pump" className={styles.impactCard}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div className={styles.inputGroup}>
                  <label>Heat Pump COP</label>
                  <Slider
                    min={2}
                    max={6}
                    step={0.1}
                    value={cop}
                    onChange={setCop}
                    marks={{ 2: '2', 3: '3', 4: '4', 5: '5', 6: '6' }}
                  />
                  <div className={styles.copIndicators}>
                    {copValues.map((c) => (
                      <Tooltip key={c.cop} title={c.label}>
                        <div 
                          className={styles.copDot}
                          style={{ 
                            backgroundColor: c.color,
                            opacity: Math.abs(cop - c.cop) < 0.5 ? 1 : 0.3,
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </div>

                <Divider />

                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Ventilation Heat Loss:</span>
                  <span className={styles.resultValue}>{formatNumber(ventilationLoss)} W</span>
                </div>

                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Heat Pump COP:</span>
                  <span className={styles.resultValue}>{cop.toFixed(1)}</span>
                </div>

                <div className={styles.formulaDisplay}>
                  <LatexRenderer 
                    latex={`P_{el} = \\frac{Q_{vent}}{COP} = \\frac{${formatNumber(ventilationLoss)}}{${cop.toFixed(1)}} = ${formatNumber(electricalPower)} \\text{ W}`} 
                  />
                </div>

                <Divider />

                <div className={styles.resultItemLarge}>
                  <span className={styles.resultLabel}>Electrical Power Required:</span>
                  <span className={styles.resultValueLarge} style={{ color: '#1890ff' }}>
                    {formatNumber(electricalPower)} W
                  </span>
                </div>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Energy Consumption Estimates" className={styles.energyCard}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div className={styles.energyItem}>
                  <ThunderboltOutlined style={{ color: '#faad14', fontSize: 24 }} />
                  <div>
                    <div className={styles.energyLabel}>Daily Consumption</div>
                    <div className={styles.energyValue}>{dailyEnergy.toFixed(2)} kWh/day</div>
                  </div>
                </div>

                <div className={styles.energyItem}>
                  <ThunderboltOutlined style={{ color: '#fa8c16', fontSize: 24 }} />
                  <div>
                    <div className={styles.energyLabel}>Monthly Consumption</div>
                    <div className={styles.energyValue}>{monthlyEnergy.toFixed(1)} kWh/month</div>
                  </div>
                </div>

                <div className={styles.energyItem}>
                  <ThunderboltOutlined style={{ color: '#f5222d', fontSize: 24 }} />
                  <div>
                    <div className={styles.energyLabel}>Heating Season (6 months)</div>
                    <div className={styles.energyValue}>{seasonalEnergy.toFixed(0)} kWh/season</div>
                  </div>
                </div>

                <Divider />

                <Alert
                  message="Cost Estimate"
                  description={
                    <span>
                      At €0.30/kWh: <strong>€{(seasonalEnergy * 0.30).toFixed(0)}/season</strong> 
                      {' '}just for ventilation losses!
                    </span>
                  }
                  type="info"
                />
              </Space>
            </Card>

            <Card title="Recommendations" className={styles.recommendationCard} style={{ marginTop: 16 }}>
              <ul className={styles.recommendationList}>
                <li>
                  <strong>Install Heat Recovery Ventilation (HRV):</strong> Reduces ventilation 
                  losses by 70-90%
                </li>
                <li>
                  <strong>Improve building airtightness:</strong> Reduce uncontrolled infiltration
                </li>
                <li>
                  <strong>Use demand-controlled ventilation:</strong> CO₂ sensors adjust airflow 
                  based on occupancy
                </li>
                <li>
                  <strong>Size heat pump correctly:</strong> Include ventilation losses in 
                  heating load calculation
                </li>
              </ul>
            </Card>
          </Col>
        </Row>

        <QoutationElement author="Energy Efficiency Principle">
          "The cheapest watt is the one you don't use. For ventilation, heat recovery is 
          almost always more cost-effective than generating additional heat."
        </QoutationElement>

        <NavigationButtons onPrevious={onPrevious} />
      </Space>
    </div>
  );
}

export default CRVentilation;
